
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Mail, AlertCircle, CheckCircle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { logger } from "@/utils/logger";
import { loginFeedback, loginProceeds } from "@/utils/loginFeedback";

export default function Login() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === "SIGNED_IN") {
      navigate("/");
    }
  });

  const sendMagicLink = async (emailAddress: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: emailAddress,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        logger.error("Falha ao enviar link de login");
        return false;
      }

      return true;
    } catch {
      logger.error("Falha ao enviar link de login");
      return false;
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);

    try {
      if (!loginProceeds({ ok: true })) {
        return;
      }

      const sent = await sendMagicLink(email);
      if (!sent) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível processar o login agora. Tente novamente.",
        });
        return;
      }

      const feedback = loginFeedback();
      setLinkSent(feedback.showLinkSent);
      toast({
        title: feedback.title,
        description: feedback.description,
      });
    } catch {
      logger.error("Falha ao processar login");
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível processar o login agora. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
            <div className="h-10 w-10">
              <img 
                src="/lovable-uploads/8fe05359-d2c7-4418-abd6-8deff9f146be.png" 
                alt="Logo Carta para o Futuro" 
                className="h-full w-full object-contain"
              />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
              Carta para o Futuro
            </span>
          </Link>
          <h2 className="text-2xl font-bold text-gray-900">
            Entre na sua conta
          </h2>
          <p className="mt-2 text-gray-600">
            Digite seu email e enviaremos um link mágico para você fazer login
          </p>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
          {linkSent ? (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">
                <div className="space-y-2">
                  <p className="font-medium">Verifique seu email</p>
                  <p className="text-sm">
                    Se <strong>{email}</strong> puder acessar a plataforma, enviamos um link de login.
                    Confira a caixa de entrada e a pasta de spam.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={loading}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading || !email}
              >
                {loading ? "Enviando..." : "Entrar"}
              </Button>
            </form>
          )}

          {!linkSent && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-start space-x-2 text-sm text-gray-600">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-700 mb-1">Não tem uma conta ainda?</p>
                  <p>Você precisa comprar um de nossos planos primeiro para ter acesso ao sistema.</p>
                  <Link 
                    to="/" 
                    className="text-blue-600 hover:text-blue-700 font-medium mt-2 inline-block"
                  >
                    Voltar para página inicial →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
